import React, { useCallback, useEffect, useRef, useState } from "react";
import { HiMiniArrowTurnUpLeft } from "react-icons/hi2";

const SWIPE_THRESHOLD = 56;
const SWIPE_MAX = 84;
const AXIS_LOCK_PX = 8;

const SwipeToReplyWrapper = ({ children, enabled, onReply }) => {
  const containerRef = useRef(null);
  const touchRef = useRef({
    startX: 0,
    startY: 0,
    locked: null,
    offsetX: 0,
  });
  const [offsetX, setOffsetX] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);

  const resetTouch = useCallback(() => {
    touchRef.current = { startX: 0, startY: 0, locked: null, offsetX: 0 };
    setOffsetX(0);
    setIsSwiping(false);
  }, []);

  const handleTouchStart = useCallback((event) => {
    if (!enabled) {
      return;
    }
    const touch = event.touches[0];
    touchRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      locked: null,
      offsetX: 0,
    };
  }, [enabled]);

  const handleTouchMove = useCallback((event) => {
    if (!enabled) {
      return;
    }

    const touch = event.touches[0];
    const { startX, startY, locked } = touchRef.current;
    const dx = touch.clientX - startX;
    const dy = touch.clientY - startY;

    if (!locked) {
      if (Math.abs(dx) < AXIS_LOCK_PX && Math.abs(dy) < AXIS_LOCK_PX) {
        return;
      }
      touchRef.current.locked = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
      if (touchRef.current.locked === "y") {
        return;
      }
    } else if (locked === "y") {
      return;
    }

    if (dx <= 0) {
      return;
    }

    event.preventDefault();
    const nextOffset = Math.min(dx * 0.72, SWIPE_MAX);
    touchRef.current.offsetX = nextOffset;
    setOffsetX(nextOffset);
    setIsSwiping(true);
  }, [enabled]);

  const handleTouchEnd = useCallback(() => {
    if (!enabled) {
      return;
    }

    if (touchRef.current.offsetX >= SWIPE_THRESHOLD && typeof onReply === "function") {
      onReply();
    }

    resetTouch();
  }, [enabled, onReply, resetTouch]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element || !enabled) {
      return undefined;
    }

    element.addEventListener("touchstart", handleTouchStart, { passive: true });
    element.addEventListener("touchmove", handleTouchMove, { passive: false });
    element.addEventListener("touchend", handleTouchEnd, { passive: true });
    element.addEventListener("touchcancel", handleTouchEnd, { passive: true });

    return () => {
      element.removeEventListener("touchstart", handleTouchStart);
      element.removeEventListener("touchmove", handleTouchMove);
      element.removeEventListener("touchend", handleTouchEnd);
      element.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [enabled, handleTouchEnd, handleTouchMove, handleTouchStart]);

  if (!enabled) {
    return children;
  }

  const hintOpacity = Math.min(offsetX / SWIPE_THRESHOLD, 1);

  return (
    <div ref={containerRef} className="swipe-reply-container">
      <div
        className="swipe-reply-hint"
        aria-hidden
        style={{ opacity: hintOpacity, visibility: isSwiping ? "visible" : "hidden" }}
      >
        <HiMiniArrowTurnUpLeft style={{ fontSize: 22 }} />
      </div>
      <div className="swipe-reply-track">
        <div
          className="swipe-reply-content"
          style={{
            transform: offsetX ? `translate3d(${offsetX}px, 0, 0)` : undefined,
            transition: isSwiping ? "none" : "transform 0.22s ease-out",
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default SwipeToReplyWrapper;
